import { storeToRefs } from "pinia";
import type { ComputedRef, MaybeRefOrGetter } from "vue";
import { resolvePriceListFlag } from "~/utils/flags/priceListFlag";
import { isFlagShownOn } from "~/utils/flags/flagSurface";
import type { ApplicationFlag, FlagSurface } from "~/types/storyblok/storyblok";

interface ProductFlagsSource {
	FlagIdSeed?: string | null;
	PriceListId?: number | null;
}

export interface ProductFlags {
	/** Flag ids to render, in display order. */
	flagIds: ComputedRef<string[]>;
	/** The price list flag, also when `showOn` hides it on this surface. */
	priceListFlag: ComputedRef<ApplicationFlag | undefined>;
	getFlagData: (flagId: string) => ApplicationFlag | undefined;
	getItemPosition: (flagId: string, index: number) => string;
}

/**
 * Builds the flag for a price list from the store's lists and Storyblok flags.
 * @param priceListId - Id of the product's price list
 * @returns The flag, or `undefined` when none applies
 */
const usePriceListFlag = (
	priceListId: MaybeRefOrGetter<number | null | undefined>
): ComputedRef<ApplicationFlag | undefined> => {
	const appStore = useAppStore();
	const { allPriceLists } = storeToRefs(useProductStore());

	return computed(() => {
		const id = toValue(priceListId);
		const priceList = id
			? allPriceLists.value.find((list) => list.Id === id)
			: undefined;
		return resolvePriceListFlag(appStore.flags, priceList, Date.now());
	});
};

/**
 * Collects a product's Norce flags and its price list flag for one surface.
 * @param product - Product with `FlagIdSeed` and `PriceListId`
 * @param surface - Where the flags render: `plp` or `pdp`
 * @returns Flag ids in display order and helpers for `ApplicationFlag`
 */
export const useProductFlags = (
	product: MaybeRefOrGetter<ProductFlagsSource | undefined>,
	surface: FlagSurface
): ProductFlags => {
	const appStore = useAppStore();
	const priceListFlag = usePriceListFlag(() => toValue(product)?.PriceListId);

	const flagIds = computed(() => {
		const norceIds = (toValue(product)?.FlagIdSeed?.split(",") ?? []).filter(
			(id) =>
				id &&
				isFlagShownOn(
					appStore.flags.find((flag) => flag.id === id),
					surface
				)
		);
		const priceListId =
			priceListFlag.value && isFlagShownOn(priceListFlag.value, surface)
				? [priceListFlag.value.id]
				: [];
		return [...norceIds, ...priceListId];
	});
	// The resolved price list flag goes first, ahead of its raw Storyblok entry.
	const flags = computed(() =>
		useApplicationFlags({
			appFlags: priceListFlag.value
				? [priceListFlag.value, ...appStore.flags]
				: appStore.flags,
			productFlags: flagIds.value
		})
	);

	return {
		flagIds,
		priceListFlag,
		getFlagData: (flagId) => flags.value.getFlagData(flagId),
		getItemPosition: (flagId, index) =>
			flags.value.getItemPosition(flagId, index)
	};
};
